import { useRef } from 'react';
import { LoanApplicationTask } from '../../shared/models';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { Field, Formik } from 'formik';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { QueryKeys } from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { getApiResponseData } from '../../shared/utils/axios';

interface ReorderDocumentListDrawerProps {
	dealGuid: string | undefined;
	documents: LoanApplicationTask[] | undefined;
	open: boolean;
	onClose: () => void;
}

export const ReorderDocumentListDrawer: React.FC<ReorderDocumentListDrawerProps> = ({
	dealGuid,
	documents,
	open,
	onClose,
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const api = useApi();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const documentsData =
		documents?.map(document => ({
			id: document.guid,
			order: document.loanApplicationDisplayOrder,
			title: document.applicationTaskTitle,
		})) ?? ([] as DocumentData[]);

	const orderMutation = useMutation(
		async ({ values }: OrderValuesMutationVariables) => {
			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.RequiredDocuments.Reorder(dealGuid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Required documents successfully re-ordered.', { variant: 'success' });
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(dealGuid));
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar('There was a problem re-ordering the documents.', { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose} PaperProps={{ sx: { width: '900px' } }}>
			<CerebroHeading size="h2" component="h3" gutterBottom>
				Re-order Documents
			</CerebroHeading>
			<CerebroText variant="small" sx={{ width: 450 }}>
				Change the order of the documents by editing the order number. Once the required documents are completed
				they will always be displayed towards the bottom.
			</CerebroText>

			<Formik
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(orderMutation.mutate)}
				initialValues={
					{
						requiredDocuments: documentsData,
					} as OrderValues
				}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm
						flatIndicator
						onSubmit={handleSubmit}
						sx={{ paddingRight: 3, width: { xs: '100%' } }}
						scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
					>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell sx={{ width: 100 }}>Order</TableCell>
										<TableCell>Title</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{values.requiredDocuments
										.sort((a, b) => a.order - b.order)
										.map((document, index) => (
											<TableRow key={document.id}>
												<TableCell>
													<Field
														key={document.order}
														as={CerebroInput}
														name={document.order}
														disabled={isSubmitting}
														value={document.order}
														onChange={(e: any) => {
															const newOrder = parseInt(e.target.value);
															const updatedDocuments = [...values.requiredDocuments];
															const documentIndex = updatedDocuments.findIndex(
																doc => doc.id === document.id
															);
															updatedDocuments[documentIndex] = {
																...updatedDocuments[documentIndex],
																order: newOrder,
															};
															setFieldValue('requiredDocuments', updatedDocuments);
														}}
													/>
												</TableCell>
												<TableCell>
													<CerebroText>{document.title}</CerebroText>
												</TableCell>
											</TableRow>
										))}
								</TableBody>
							</Table>
						</TableContainer>

						<CerebroPrimaryButton loading={isSubmitting} type="submit" sx={{ marginTop: 6 }}>
							Save Changes
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type OrderValuesMutationVariables = FormikUtils.MutateOnSubmitVariables<OrderValues>;
interface OrderValues {
	requiredDocuments: DocumentData[];
}

interface DocumentData {
	id: string;
	order: number;
	title: string;
}
